import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import I18n from 'i18n-js'

import { useSegmentsStore } from '@app/insights/store/segments'
import * as api from './api'
import type { PinnedIdsResponse } from './api'
import { firmKey, partnerKey, segmentKey, recentKey } from './segment-key'
import {
  matchesSearch,
  matchesVisibility,
  matchesPublisher,
  sortSegments,
  fakePinPendingKey,
} from './utils'
import type { FakePinKey } from './utils'
import type { SegmentErrorResponse } from '@app/insights/types'
import { publisherOptions, visibilityOptions } from './constants'
import type {
  RecentSegment,
  Segment,
  VisibilityFilter,
  PublisherFilter,
  SortOption,
} from './types'
const RECENTLY_VISITED_LIMIT = 3

type InitPayload = {
  recentSegments: RecentSegment[]
  pinnedSegmentIds: number[]
  pinnedPartnerSegmentIds: number[]
  newlyAddedPartnerSegmentIds: number[]
  dismissedFakePinKeys: string[]
}

export const useSegmentsOverviewStore = defineStore('segments-overview', () => {
  // The list of segments lives in useSegmentsStore — the same store the
  // EditSegmentModal mutates on save and the global deleteSegment removes
  // from. Reading from it here means edits/deletes propagate to this page
  // automatically.
  const segmentsStore = useSegmentsStore()

  const isLoading = ref(false)
  const loadError = ref(false)
  const searchValue = ref('')
  const visibilityFilter = ref<VisibilityFilter>(visibilityOptions.ANY)
  const publisherFilter = ref<PublisherFilter>(publisherOptions.ALL)
  const sortOption = ref<SortOption>('default')

  // Keys of segments with a pin/unpin request in flight. Used to make the pin
  // icon inert so a double-click can't fire pin-then-unpin back-to-back.
  const pendingPinKeys = ref<Set<string>>(new Set())

  const recentSegments = ref<RecentSegment[]>([])
  const pinnedSegmentIds = ref<number[]>([])
  const pinnedPartnerSegmentIds = ref<number[]>([])
  const newlyAddedPartnerSegmentIds = ref<number[]>([])
  const dismissedFakePinKeys = ref<Set<string>>(new Set())

  const segments = computed<Segment[]>(() => {
    return segmentsStore.segments || []
  })

  function init(payload: InitPayload): void {
    recentSegments.value = payload.recentSegments
    pinnedSegmentIds.value = payload.pinnedSegmentIds
    pinnedPartnerSegmentIds.value = payload.pinnedPartnerSegmentIds
    newlyAddedPartnerSegmentIds.value = payload.newlyAddedPartnerSegmentIds
    dismissedFakePinKeys.value = new Set(payload.dismissedFakePinKeys)
  }

  function seedPins(payload: PinnedIdsResponse): void {
    pinnedSegmentIds.value = payload.segment_ids
    pinnedPartnerSegmentIds.value = payload.partner_segment_ids
  }

  async function fetchAll(): Promise<void> {
    isLoading.value = true
    loadError.value = false
    try {
      await segmentsStore.fetchSegments()
    } catch {
      // Surface the failure instead of letting it look like an empty firm.
      loadError.value = true
    } finally {
      isLoading.value = false
    }
  }

  function setSearch(value: string): void {
    searchValue.value = value
  }

  function filterByVisibility(value: VisibilityFilter): void {
    visibilityFilter.value = value
  }

  function filterByPublisher(value: PublisherFilter): void {
    publisherFilter.value = value
  }

  function sortBy(value: SortOption): void {
    sortOption.value = value
  }

  // Normalize the search term once per change instead of per segment per render.
  const searchTerm = computed(() => searchValue.value.trim().toLowerCase())

  const filteredSegments = computed((): Segment[] => {
    const filtered = segments.value.filter(
      (s) =>
        matchesSearch(s, searchTerm.value) &&
        matchesPublisher(s, publisherFilter.value) &&
        matchesVisibility(s, visibilityFilter.value)
    )
    return sortSegments(filtered, sortOption.value)
  })

  // Sort reorders rather than narrows the list, so it doesn't count as a
  // filter for the purpose of hiding the side panel.
  const hasActiveFilters = computed(
    (): boolean =>
      searchTerm.value !== '' ||
      publisherFilter.value !== publisherOptions.ALL ||
      visibilityFilter.value !== visibilityOptions.ANY
  )

  // A computed whose value is itself a function ("parameterized getter"):
  // Pinia can only tell a setup store's actions and getters apart by whether
  // the returned value is a plain function or a ref/computed. A plain
  // function taking an argument is indistinguishable from an action, so
  // tooling that stubs actions (e.g. createTestingPinia) would stub this too.
  // Wrapping it in computed() marks it unambiguously as a getter.
  const isPinned = computed(() => (segment: Segment): boolean => {
    if (segment.partner_segment_id) {
      return pinnedPartnerSegmentIds.value.includes(segment.partner_segment_id)
    }
    return pinnedSegmentIds.value.includes(segment.id)
  })

  const pinnedSegments = computed((): Segment[] => {
    const order = new Map<string, number>()
    let idx = 0
    pinnedSegmentIds.value.forEach((id) => order.set(firmKey(id), idx++))
    pinnedPartnerSegmentIds.value.forEach((id) =>
      order.set(partnerKey(id), idx++)
    )

    return segments.value
      .filter((s) => order.has(segmentKey(s)))
      .sort(
        (a, b) =>
          (order.get(segmentKey(a)) ?? 0) - (order.get(segmentKey(b)) ?? 0)
      )
  })

  const newlyAddedSegments = computed((): Segment[] => {
    const ids = new Set(newlyAddedPartnerSegmentIds.value)
    return segments.value.filter(
      (s) => s.partner_segment_id && ids.has(s.partner_segment_id)
    )
  })

  // Resolve each recent reference (backend order, newest-first) to its full
  // segment and keep the most recent few. Recents whose segment isn't in the
  // list (e.g. no longer visible) drop out.
  const recentlyVisitedSegments = computed((): Segment[] => {
    const byKey = new Map(segments.value.map((s) => [segmentKey(s), s]))
    return recentSegments.value
      .map((recent) => byKey.get(recentKey(recent)))
      .filter((s): s is Segment => Boolean(s))
      .slice(0, RECENTLY_VISITED_LIMIT)
  })

  const isPinPending = computed(
    () =>
      (segment: Segment): boolean =>
        pendingPinKeys.value.has(segmentKey(segment))
  )

  async function togglePin(segment: Segment): Promise<void> {
    const key = segmentKey(segment)
    // Ignore re-entrant toggles while a request for this segment is in flight,
    // otherwise a double-click would fire pin then unpin (or vice-versa) and
    // the final state would depend on response ordering.
    if (pendingPinKeys.value.has(key)) return

    const target = {
      segmentId: segment.partner_segment_id ? null : segment.id,
      partnerSegmentId: segment.partner_segment_id ?? null,
    }
    const pinned = isPinned.value(segment)
    pendingPinKeys.value = new Set(pendingPinKeys.value).add(key)
    try {
      const response = pinned
        ? await api.unpinSegment(target)
        : await api.pinSegment(target)
      // The server returns the full pin set, so we only adopt it on success.
      // A failed request leaves the local state untouched (consistent with
      // the server) rather than wiping it.
      pinnedSegmentIds.value = response.segment_ids
      pinnedPartnerSegmentIds.value = response.partner_segment_ids
    } catch (e) {
      const message = (e as SegmentErrorResponse).body.errorMessage
      window.SilverfinFlash.addFlash(
        'error',
        message || I18n.t('companies_overview.segments.pin_error')
      )
    } finally {
      const next = new Set(pendingPinKeys.value)
      next.delete(key)
      pendingPinKeys.value = next
    }
  }

  function dropPinForSegment(segmentId: number): void {
    pinnedSegmentIds.value = pinnedSegmentIds.value.filter(
      (id) => id !== segmentId
    )
  }

  const isFakePinDismissed = computed(
    () =>
      (key: FakePinKey): boolean =>
        dismissedFakePinKeys.value.has(key)
  )

  const isFakePinPending = computed(
    () =>
      (key: FakePinKey): boolean =>
        pendingPinKeys.value.has(fakePinPendingKey(key))
  )

  async function dismissFakePin(key: FakePinKey): Promise<void> {
    const pendingKey = fakePinPendingKey(key)
    if (pendingPinKeys.value.has(pendingKey)) return

    pendingPinKeys.value = new Set(pendingPinKeys.value).add(pendingKey)
    try {
      await api.dismissFakePin(key)
      dismissedFakePinKeys.value = new Set(dismissedFakePinKeys.value).add(key)
    } catch (e) {
      const message = (e as SegmentErrorResponse).body.errorMessage
      window.SilverfinFlash.addFlash(
        'error',
        message || I18n.t('companies_overview.segments.pin_error')
      )
    } finally {
      const next = new Set(pendingPinKeys.value)
      next.delete(pendingKey)
      pendingPinKeys.value = next
    }
  }

  return {
    segments,
    isLoading,
    loadError,
    searchValue,
    visibilityFilter,
    publisherFilter,
    sortOption,
    recentSegments,
    pinnedSegmentIds,
    pinnedPartnerSegmentIds,
    newlyAddedPartnerSegmentIds,
    dismissedFakePinKeys,
    filteredSegments,
    hasActiveFilters,
    pinnedSegments,
    newlyAddedSegments,
    recentlyVisitedSegments,
    init,
    seedPins,
    fetchAll,
    setSearch,
    filterByVisibility,
    filterByPublisher,
    sortBy,
    isPinned,
    isPinPending,
    togglePin,
    dropPinForSegment,
    isFakePinDismissed,
    isFakePinPending,
    dismissFakePin,
  }
})
